import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { formatDayText } from '../events/fields';
import { useUrlState } from '../nav/urlState';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import { BarterEntryForm } from './BarterEntryForm';
import { BARTER_KEY, BARTER_RESOURCE, balanceSide, DIRECTION_LABELS, type ContractorBalance, type ContractorCard, type EntryView } from './types';
import './barter.css';
import { DialogLayer } from '../ui/Dialog';
import { leaveGuarded } from '../ui/unsaved';

function Balance({ grosze }: { grosze: number | undefined }) {
  if (grosze === undefined) return null;
  if (grosze === 0) return <span className="muted">rozliczony</span>;
  return (
    <span className={grosze > 0 ? 'barter-ours' : 'barter-theirs'}>
      {formatPln(Math.abs(grosze))} <span className="small">{balanceSide(grosze)}</span>
    </span>
  );
}

/** Deleting changes the balance and has no undo: asked in an in-app dialog first (like contractors and catalog). */
function DeleteDialog({ entry, onClose }: { entry: EntryView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/api/barter/entries/${entry.id}?version=${entry.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BARTER_KEY });
      onClose();
    },
  });
  const conflict = remove.error instanceof ApiError && remove.error.status === 409;
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="barter-delete-title">
        <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="barter-delete-title">Usunąć wpis barteru?</h2>
        <p>
          „{entry.description}” ({formatDayText(entry.occurredOn)}) zniknie z historii kontrahenta i przestanie liczyć się do salda. W historii zmian zostaje wpis o usunięciu.
        </p>
        {conflict && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: BARTER_KEY });
              onClose();
            }}
          />
        )}
        {remove.error && !conflict && (
          <p className="alert" role="alert">
            {remove.error instanceof ApiError ? remove.error.message : 'Nie udało się usunąć'}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : 'Usuń'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * "Finanse → Barter" (F43, spec E3 §6.6): contractors we settle with a service or gear instead of money — open
 * balances first — and, for the picked one (`?kontrahent=`), the history of entries. One main button: "Dodaj wpis".
 */
export function BarterPage() {
  const { me } = useMe();
  const { params, update } = useUrlState();
  const picked = params.get('kontrahent') ?? '';
  const q = params.get('q') ?? '';
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<EntryView | null>(null);
  const list = useQuery({
    queryKey: [...BARTER_KEY, 'list', q],
    queryFn: () => api<{ contractors: ContractorBalance[] }>(`/api/barter?q=${encodeURIComponent(q)}`),
  });
  const card = useQuery({
    queryKey: [...BARTER_KEY, 'contractor', picked],
    enabled: picked !== '',
    queryFn: () => api<ContractorCard>(`/api/barter/contractors/${picked}`),
  });
  if (!me) return null;
  const seeValue = fieldVisible(me, BARTER_RESOURCE, 'valueGrosze');
  const seeBalance = fieldVisible(me, BARTER_RESOURCE, 'balanceGrosze');
  const rows = list.data?.contractors ?? [];
  const current = card.data;
  // another contractor or entry: an open form with changes asks first (owner 2026-10-23 21:14)
  const pick = (id: string | null) =>
    leaveGuarded(() => {
      setAdding(false);
      setEditing(null);
      update({ kontrahent: id });
    });

  return (
    <section className="page barter-page" data-feature-id="barter.list">
      <div className="barter-head">
        <p className="muted">Usługi i sprzęt rozliczane zamiast pieniędzy. Saldo „na naszą korzyść” — kontrahent jest nam winien; „na ich korzyść” — my im.</p>
        {can(me, BARTER_RESOURCE, 'create') && !adding && (
          <button className="btn primary" type="button" onClick={() => leaveGuarded(() => { setEditing(null); setAdding(true); })}>
            Dodaj wpis
          </button>
        )}
      </div>
      {adding && <BarterEntryForm contractor={current ? { id: current.contractorId, name: current.name } : null} onClose={() => setAdding(false)} />}

      <label className="field barter-search">
        <span>Szukaj kontrahenta</span>
        <input type="search" value={q} onChange={(e) => update({ q: e.target.value || null })} />
      </label>
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać barteru'}</p>}
      {list.data && rows.length === 0 && <p className="muted">{q ? 'Nikt nie pasuje do wyszukiwania.' : 'Nie ma jeszcze wpisów barteru.'}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table barter-table" aria-label="Kontrahenci z barterem">
            <thead>
              <tr>
                <th scope="col">Kontrahent</th>
                {seeBalance && (
                  <th scope="col" className="num">
                    Saldo
                  </th>
                )}
                <th scope="col" className="wide-only">
                  Ostatni wpis
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.contractorId} className={c.contractorId === picked ? 'is-picked' : undefined}>
                  <td>
                    <button type="button" className="contractor-link" aria-current={c.contractorId === picked ? 'true' : undefined} onClick={() => pick(c.contractorId)}>
                      {c.name}
                    </button>
                    {!seeBalance && c.open && <span className="muted"> · nierozliczony</span>}
                  </td>
                  {seeBalance && (
                    <td className="num">
                      <Balance grosze={c.balanceGrosze} />
                    </td>
                  )}
                  <td className="wide-only">{formatDayText(c.lastEntryOn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {picked !== '' && current && (
        <>
          <h2>
            {current.name}
            {seeBalance && (
              <>
                {' · '}
                <Balance grosze={current.balanceGrosze} />
              </>
            )}
          </h2>
          {current.entries.length === 0 ? (
            <p className="muted">Ten kontrahent nie ma wpisów.</p>
          ) : (
            <div className="table-wrap">
              <table className="table barter-table" aria-label={`Historia barteru: ${current.name}`}>
                <thead>
                  <tr>
                    <th scope="col">Data</th>
                    <th scope="col">Kierunek</th>
                    <th scope="col">Opis</th>
                    {seeValue && (
                      <th scope="col" className="num">
                        Wartość
                      </th>
                    )}
                    <th scope="col" className="tpl-actions-col">
                      <span className="visually-hidden">Działania</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {current.entries.map((e) =>
                    editing === e.id ? (
                      <tr key={e.id} className="row-editor">
                        <td colSpan={seeValue ? 5 : 4}>
                          <BarterEntryForm entry={e} onClose={() => setEditing(null)} />
                        </td>
                      </tr>
                    ) : (
                      <tr key={e.id}>
                        <td className="nowrap">{formatDayText(e.occurredOn)}</td>
                        <td>{DIRECTION_LABELS[e.direction]}</td>
                        <td>
                          {e.description}
                          {e.eventName && <span className="muted"> · {e.eventName}</span>}
                          {e.source === 'platnosc' && <span className="muted barter-source"> · wpłata barterem</span>}
                        </td>
                        {seeValue && <td className="num">{e.valueGrosze !== undefined ? formatPln(e.valueGrosze) : ''}</td>}
                        <td className="tpl-actions">
                          {e.source === 'reczny' && (
                            <RowMenu
                              label={`Więcej działań: ${e.description}`}
                              actions={[
                                ...(can(me, BARTER_RESOURCE, 'edit') ? [{ label: 'Edytuj', onSelect: () => leaveGuarded(() => { setAdding(false); setEditing(e.id); }) }] : []),
                                ...(can(me, BARTER_RESOURCE, 'delete') ? [{ label: 'Usuń', danger: true, onSelect: () => setDeleting(e) }] : []),
                              ]}
                            />
                          )}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {deleting && <DeleteDialog entry={deleting} onClose={() => setDeleting(null)} />}
    </section>
  );
}
